import { RadioGroup } from "@headlessui/react";
import clsx from "clsx";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AddDeviceFormType } from "@/apps/partner/types";
import { RadioIndicator } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";

type SimTypeOption = {
  value: NonNullable<AddDeviceFormType["simType"]>;
  title: string;
  description: string;
  testId: string;
};

export const PrepareBasketSimSelection = () => {
  const { t } = useTranslation("partnerPortal", {
    keyPrefix: "addDevice.step1",
  });
  const { t: tDefault } = useTranslation();
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AddDeviceFormType>();

  const simType = watch("simType");

  const simTypeOptions: SimTypeOption[] = [
    {
      value: "TRIPLESIM",
      title: tDefault("Common.products.simCard"),
      description: t("simCard.description"),
      testId: "triplesim-tile",
    },
    {
      value: "ESIM",
      title: tDefault("Common.products.esim"),
      description: t("esim.description"),
      testId: "esim-tile",
    },
  ];

  return (
    <div
      data-testid="select-sim"
      id="partner-select-sim"
      className="flex w-full flex-col gap-6"
    >
      <CardContainer className="rounded-lg p-4" variant="white">
        <RadioGroup
          aria-label={tDefault("portal:common.device.attributes.simType")}
          value={simType ?? ""}
          onChange={(value: AddDeviceFormType["simType"] | "") =>
            value &&
            setValue("simType", value, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
        >
          <div className="grid gap-6 tablet:grid-cols-2">
            {simTypeOptions.map(({ value, title, description, testId }) => (
              <RadioGroup.Option
                key={value}
                data-testid={testId}
                className="outline-none"
                value={value}
              >
                {({ checked }) => (
                  <div
                    className={clsx(
                      "flex h-full cursor-pointer flex-col gap-2 rounded-lg bg-surface-accent-ivory p-4",
                      checked && "outline outline-2 outline-foreground-primary",
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-col gap-1">
                        <p className="text-headline-sm font-semibold leading-md text-foreground-primary">
                          {title}
                        </p>
                        <p className="text-body-sm text-foreground-secondary">
                          {description}
                        </p>
                      </div>
                      <RadioIndicator checked={checked} />
                    </div>
                  </div>
                )}
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      </CardContainer>
      {errors.simType && !simType && (
        <span className="text-body-sm text-foreground-error">
          {String(errors.simType.message)}
        </span>
      )}
    </div>
  );
};
